import * as yup from 'yup';
import { INVALID_PHONE_NUMBER_REGEX } from './constants';

export const FULL_NAME_REGEX = /^[a-zA-Z\u0600-\u06FF]{2,}(\s+[a-zA-Z\u0600-\u06FF]{2,})+$/;

/**
 * Returns whether `value` is a valid Iraqi mobile **national** number (10 digits: `7[56789]` + 8 digits).
 * Same rule as login `PhoneNumberContent` Yup `.matches(INVALID_PHONE_NUMBER_REGEX, ...)`.
 *
 * @param value - Typically a trimmed checkout field string; `undefined` / empty is invalid.
 */
export const isIraqiMobileNationalNumberValid = (value: string | undefined): boolean => {
  if (!value) return false;
  return INVALID_PHONE_NUMBER_REGEX.test(value.trim());
};

/**
 * Validates a full name: at least two words, each word at least 2 characters, letters Arabic/Latin only.
 *
 * @param value - Raw string; empty / undefined is invalid.
 */
export const validateFullName = (value: string | undefined): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  const normalized = trimmed.replace(/\s+/g, ' ');
  const words = normalized.split(' ');

  if (words.length < 2) return false;

  return words.every((word) => word.length >= 2) && FULL_NAME_REGEX.test(normalized);
};

/**
 * Yup string schema for checkout ticket-holder phone: required, trimmed, national format only.
 * Error message keys: `checkout.fieldIsRequired`, `checkout.phoneNumber.invalid`.
 */
export const iraqiPhoneNumberSchema = yup
  .string()
  .transform((value) => value?.trim())
  .required('checkout.fieldIsRequired')
  .test('iraqi-mobile-national', 'checkout.phoneNumber.invalid', (value) => {
    if (!value) return false;
    return isIraqiMobileNationalNumberValid(value);
  });

/**
 * Yup string schema for checkout full name: required, trimmed, collapsed whitespace, pattern + length.
 * Error keys: `checkout.fieldIsRequired`, `checkout.name.invalid`, `checkout.name.minLength`, `checkout.name.maxLength`.
 */
export const fullNameSchema = yup
  .string()
  .transform((value) => value?.trim().replace(/\s+/g, ' '))
  .required('checkout.fieldIsRequired')
  .test('full-name', 'checkout.name.invalid', (value) => {
    if (!value) return false;
    return validateFullName(value);
  })
  .min(3, 'checkout.name.minLength')
  .max(50, 'checkout.name.maxLength');
